import { findService, formatPoints, POINTS_EXPIRY_WARNING_DAYS } from "@iec-id/shared";
import { GrantTargetTypeSchema } from "@iec-id/shared/contracts/points";
import { notify, SectionCard, Tabs, TabsPanel } from "@iec-id/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { EarnTasksCard } from "@/components/points/earn-tasks-card";
import { POINTS_NS } from "@/components/points/i18n";
import { InviteCard } from "@/components/points/invite-card";
import { MilestoneCard } from "@/components/points/milestone-card";
import { PayWithPointsDialog } from "@/components/points/pay-with-points-dialog";
import { PointsEarnGuide } from "@/components/points/points-earn-guide";
import { PointsHistory } from "@/components/points/points-history";
import { PointsSpendGuide } from "@/components/points/points-spend-guide";
import { PointsSummary } from "@/components/points/points-summary";
import { daysUntil, formatDayMonth } from "@/lib/format";
import { useStickyColumn } from "@/lib/sticky-column";
import { api } from "@/lib/trpc";

/**
 * Счёт, присланный сервисом на погашение Баллами, приезжает в адресной строке:
 * сервис уводит человека на `id.iec.exchange/points?pay=…`, мы выпускаем грант
 * и возвращаем скидку. Своей формы «введите сумму» здесь нет и быть не должно —
 * базу погашения считает сервис, а долю и потолок проверяет модуль `points`.
 */
const PointsSearchSchema = z.object({
	/**
	 * Какую вкладку открыть. Нужен ссылкам ИЗВНЕ раздела: уведомление о
	 * начислении ведёт в историю, кнопка «Потратить» — в «Куда потратить».
	 * Без параметра оба приземляли человека на «Как получать», то есть на
	 * список того, чего он ещё не сделал, вместо ответа на его вопрос.
	 */
	tab: z.enum(["history", "earn", "spend"]).optional(),
	pay: GrantTargetTypeSchema.optional(),
	service: z.string().min(2).max(64).optional(),
	target: z.string().min(1).max(128).optional(),
	/** Наша часть чека в minor units. */
	eligible: z.coerce.number().int().nonnegative().optional(),
	total: z.coerce.number().int().nonnegative().optional(),
	/**
	 * Куда вернуть человека с купоном. Адрес НЕ доверенный: он проверяется
	 * по реестру сервисов (`resolveGrantReturnUrl`) и принимается только если
	 * origin совпал с адресом того же `service`. Схема здесь лишь ограничивает
	 * длину — решение о переходе принимает allowlist, а не эта строка.
	 */
	return: z.string().min(8).max(2048).optional(),
});

// НАЗВАНИЯ СЧЁТА В АДРЕСНОЙ СТРОКЕ НЕТ И БЫТЬ НЕ ДОЛЖНО.
//
// Раньше здесь стоял `name` до 200 символов, и он уходил прямо в описание
// модалки «Оплатить Баллами». XSS это не давало (React экранирует), но давало
// кое-что похуже: любой человек мог прислать ссылку вида
// `id.iec.exchange/points?pay=programme&…&name=Перевод+в+пользу+…` и получить
// НАШУ модалку с ЧУЖИМ текстом. Сумму и цель проверяет сервер (`quoteGrant`),
// а название не проверял никто.
//
// Поэтому подпись собирается только из проверенного: сервис — из реестра
// `ECOSYSTEM_SERVICES`, тип цели — из перечисления контракта, оба через i18n.
// Настоящее имя цели отдаёт СЕРВЕР: `quoteGrant.targetName` собирается из
// реестра сервисов и списка тарифов IEC+. Подпись из этого файла — запасная:
// она нужна целям, названия которых у нас нет (каталог программ и курсов живёт
// в сервисе, а не в IEC ID).

export const Route = createFileRoute("/points")({
	component: PointsSection,
	validateSearch: PointsSearchSchema,
});

type TabValue = "history" | "earn" | "spend";

function PointsSection() {
	const { t } = useTranslation(POINTS_NS);
	const search = Route.useSearch();
	const navigate = useNavigate();
	const [tab, setTab] = useState<TabValue | null>(null);
	// Один и тот же элемент: `scrollIntoView` из сводки ведёт к блоку
	// приглашений, и он же — липкая правая колонка. Второй ref завёл бы
	// два владельца у одного узла.
	const inviteRef = useStickyColumn<HTMLElement>();
	// Вкладка по умолчанию — «Как получать» (решение владельца 23.08.2026).
	// Раньше выбор зависел от баланса, и человек с Баллами приземлялся в
	// историю: список того, что уже случилось, вместо того, что можно сделать.
	// Ждать сводку для этого больше не нужно — вкладка не мигает при загрузке.
	// Выбор человека (`tab`) перебивает адресную строку: она задаёт только
	// точку входа, а не запирает раздел на одной вкладке.
	const resolved: TabValue = tab ?? search.tab ?? "earn";

	useExpiryNotice();

	const payOpen = Boolean(search.pay && search.service && search.target && search.eligible);

	const tabItems = [
		{
			value: "history",
			label: <TabLabel long={t("tabs.history")} short={t("tabs.historyShort")} />,
		},
		{ value: "earn", label: <TabLabel long={t("tabs.earn")} short={t("tabs.earnShort")} /> },
		{ value: "spend", label: <TabLabel long={t("tabs.spend")} short={t("tabs.spendShort")} /> },
	];

	return (
		<div className="flex flex-col gap-2.5 lg:flex-row lg:items-start">
			<div className="flex w-full min-w-0 flex-grow flex-col gap-1">
				<SectionCard
					title={t("title")}
					level="page"
					hint={t("hint")}
					hintTriggerLabel={t("common:action.hint")}
				>
					<PointsSummary
						onInvite={() => inviteRef.current?.scrollIntoView({ behavior: "smooth" })}
						onSpend={() => setTab("spend")}
					/>
					<Tabs
						items={tabItems}
						value={resolved}
						onValueChange={(next) => setTab(next as TabValue)}
						label={t("title")}
					>
						<TabsPanel value="history">
							<PointsHistory />
						</TabsPanel>
						<TabsPanel value="earn">
							{/* Сначала личные задачи, потом правила программы: человек
							    пришёл сюда за «что мне сделать», а не за прейскурантом. */}
							<div className="flex flex-col gap-1">
								<EarnTasksCard place="points" />
								<PointsEarnGuide />
							</div>
						</TabsPanel>
						<TabsPanel value="spend">
							<PointsSpendGuide />
						</TabsPanel>
					</Tabs>
				</SectionCard>
			</div>

			<aside
				ref={inviteRef}
				className="flex w-full flex-col gap-1 lg:sticky lg:w-[310px] lg:shrink-0"
			>
				<InviteCard />
				<MilestoneCard />
			</aside>

			{payOpen && search.pay && search.service && search.target && search.eligible ? (
				<PayWithPointsDialog
					open
					onOpenChange={(open) => {
						if (!open) void navigate({ to: "/points", search: {} });
					}}
					serviceId={search.service}
					scope={{ targetType: search.pay, targetId: search.target, ownServicesOnly: true }}
					eligibleMinor={search.eligible}
					totalMinor={search.total}
					returnTo={search.return}
					targetName={`${t(`spend.target.${search.pay}`)} · ${findService(search.service)?.name ?? search.service}`}
				/>
			) : null}
		</div>
	);
}

/** Короткая подпись вкладки на мобильном — «Получать» вместо «Как получать». */
function TabLabel({ long, short }: { long: string; short: string }) {
	return (
		<>
			<span className="sm:hidden">{short}</span>
			<span className="hidden sm:inline">{long}</span>
		</>
	);
}

/**
 * Предупреждение о ближайшем сгорании (тост «3 200 Баллов сгорают 14 ноября»).
 * Показывается один раз за визит в раздел и только внутри окна предупреждения
 * из `POINTS_EXPIRY_WARNING_DAYS` — иначе он перестанет что-либо значить.
 */
function useExpiryNotice() {
	const { t, i18n } = useTranslation(POINTS_NS);
	const locale = i18n.language;
	const summary = api.points.summary.useQuery();
	const shown = useRef(false);
	const expiry = summary.data?.nextExpiry ?? null;

	useEffect(() => {
		if (shown.current || !expiry) return;
		const left = daysUntil(expiry.expiresAt);
		const window = Math.max(...POINTS_EXPIRY_WARNING_DAYS);
		if (left === null || left < 0 || left > window) return;
		shown.current = true;
		notify.info(
			t("grant.expiryNotice", {
				points: formatPoints(expiry.points, locale),
				date: formatDayMonth(expiry.expiresAt, locale),
			}),
		);
	}, [expiry, t, locale]);
}
